import type {
  BrandingFields,
  BrandingImageInfo,
  BrandingRecord,
  UploadedImage,
  WordingSet,
} from "@/api/branding";
import { DELIVERY_IMAGE_BUDGET_BYTES, SIGNATURE_MAX_BYTES } from "@/branding/images";

/**
 * The branding editor's draft and how it becomes a save (contract 4.9 PUT).
 *
 * A save is the organisation's **full own set**: a key the draft leaves empty is sent
 * absent, which unsets it, and the organisation then takes that unit from its parent (D19)
 * or the neutral default. Text is sent as typed, apart from blank wording lines, which the
 * server refuses and which a lab types to space paragraphs.
 */

export const TEXT_FIELDS = [
  "display_name",
  "display_name_ar",
  "sender_name",
  "sender_name_ar",
  "contact_email",
  "contact_phone",
  "contact_address",
  "contact_website",
  "footer_line",
  "terms_url",
  "privacy_url",
  "master_signature_holder_name",
  "prepared_by",
  "timezone",
  "primary_color",
  "ink_color",
] as const;
export type TextField = (typeof TEXT_FIELDS)[number];

export const IMAGE_FIELDS = ["logo_sha256", "stamp_sha256", "master_signature_sha256"] as const;
export type ImageField = (typeof IMAGE_FIELDS)[number];

export const SWITCH_FIELDS = [
  "show_approved_by",
  "show_approver_email",
  "show_prepared_by",
] as const;
export type SwitchField = (typeof SWITCH_FIELDS)[number];
/** A report-details switch: unset takes the parent's, or on by default (D5). */
export type SwitchValue = "inherit" | "show" | "hide";

export const WORDING_MESSAGES = ["report_ready", "report_updated", "pin_reissued"] as const;
export type WordingMessageKey = (typeof WORDING_MESSAGES)[number];

/** One email as the editor holds it: the body lines as one text, a line per line. */
export interface WordingDraftMessage {
  subject: string;
  body: string;
  /** `pin_reissued` only: the body used when one day of the link is left. */
  body_one_day: string;
}
export type WordingDraft = Record<WordingMessageKey, WordingDraftMessage>;

export interface BrandingDraft {
  text: Record<TextField, string>;
  images: Record<ImageField, string>;
  switches: Record<SwitchField, SwitchValue>;
  /** Language code to its three emails, in the order the lab added them. */
  wording: Record<string, WordingDraft>;
}

function emptyMessage(): WordingDraftMessage {
  return { subject: "", body: "", body_one_day: "" };
}

export function emptyWording(): WordingDraft {
  return {
    report_ready: emptyMessage(),
    report_updated: emptyMessage(),
    pin_reissued: emptyMessage(),
  };
}

function wordingDraftOf(set: WordingSet): WordingDraft {
  const draft = emptyWording();
  for (const key of WORDING_MESSAGES) {
    const message = set[key];
    if (message === undefined) continue;
    draft[key] = {
      subject: message.subject,
      body: message.body.join("\n"),
      body_one_day: key === "pin_reissued" ? (set.pin_reissued?.body_one_day ?? []).join("\n") : "",
    };
  }
  return draft;
}

/** The editor's draft of an organisation's own saved values. */
export function draftOf(own: BrandingFields): BrandingDraft {
  const text = Object.fromEntries(TEXT_FIELDS.map((f) => [f, own[f] ?? ""])) as Record<
    TextField,
    string
  >;
  const images = Object.fromEntries(IMAGE_FIELDS.map((f) => [f, own[f] ?? ""])) as Record<
    ImageField,
    string
  >;
  const switches = Object.fromEntries(
    SWITCH_FIELDS.map((f) => [f, own[f] === undefined ? "inherit" : own[f] ? "show" : "hide"]),
  ) as Record<SwitchField, SwitchValue>;
  const wording = Object.fromEntries(
    Object.entries(own.email_wording ?? {}).map(([lang, set]) => [lang, wordingDraftOf(set)]),
  );
  return { text, images, switches, wording };
}

/** Body text to the lines the server takes: one per line, blank lines skipped. */
export function linesOf(text: string): string[] {
  return text
    .split(/\r?\n/)
    .map((line) => line.replace(/\s+$/, ""))
    .filter((line) => line.trim() !== "");
}

/** The draft as the full own set a save sends. Empty means unset, so the key is absent. */
export function fieldsOf(draft: BrandingDraft): BrandingFields {
  const fields: BrandingFields = {};
  for (const f of TEXT_FIELDS) {
    const value = draft.text[f];
    if (value.trim() !== "") {
      // Colours are compared lowercase by the server, so they are sent lowercase.
      fields[f] = f === "primary_color" || f === "ink_color" ? value.trim().toLowerCase() : value;
    }
  }
  for (const f of IMAGE_FIELDS) {
    if (draft.images[f] !== "") fields[f] = draft.images[f];
  }
  for (const f of SWITCH_FIELDS) {
    if (draft.switches[f] !== "inherit") fields[f] = draft.switches[f] === "show";
  }
  const wording: Record<string, WordingSet> = {};
  for (const [lang, set] of Object.entries(draft.wording)) {
    const out: WordingSet = {};
    for (const key of WORDING_MESSAGES) {
      const m = set[key];
      if (
        m.subject.trim() === "" &&
        linesOf(m.body).length === 0 &&
        linesOf(m.body_one_day).length === 0
      ) {
        continue;
      }
      if (key === "pin_reissued") {
        out.pin_reissued = { subject: m.subject, body: linesOf(m.body) };
        const oneDay = linesOf(m.body_one_day);
        if (oneDay.length > 0) out.pin_reissued.body_one_day = oneDay;
      } else {
        out[key] = { subject: m.subject, body: linesOf(m.body) };
      }
    }
    // An empty language is dropped. A partly written one is sent, so the server can say
    // which email is missing (`wording_incomplete`) rather than the lab losing what it typed.
    if (Object.keys(out).length > 0) wording[lang] = out;
  }
  if (Object.keys(wording).length > 0) fields.email_wording = wording;
  return fields;
}

/**
 * The value a unit takes after a save, as far as the editor can tell: the draft's own
 * value, else what the organisation inherits now. A unit the organisation set itself and the
 * draft clears falls to its parent, which the read does not carry, so it shows as unset
 * here and the server's preview is the authority on it.
 */
export function inherited(
  record: BrandingRecord | undefined,
  unit: string,
  organizationId: string,
): { value: unknown; source: string | null } | null {
  const resolved = record?.resolved[unit];
  if (resolved === undefined || resolved.value === null || resolved.value === undefined) {
    return null;
  }
  if (resolved.source_organization_id === organizationId) {
    return null;
  }
  return { value: resolved.value, source: resolved.source_organization_id };
}

/** Image metadata by hash, the read's `images` and this session's uploads together. */
export type ImageInfoByHash = Record<string, BrandingImageInfo>;

export function imageInfoOf(
  record: BrandingRecord | undefined,
  uploads: Record<string, UploadedImage>,
): ImageInfoByHash {
  const info: ImageInfoByHash = { ...(record?.images ?? {}) };
  for (const [sha, image] of Object.entries(uploads)) {
    info[sha] = {
      media_type: image.media_type,
      byte_size: image.byte_size,
      width_px: image.width_px,
      height_px: image.height_px,
    };
  }
  return info;
}

/** The image hash a slot would carry after a save: own, else inherited (never the master signature). */
export function effectiveImage(
  draft: BrandingDraft,
  record: BrandingRecord | undefined,
  field: ImageField,
  organizationId: string,
): string | null {
  if (draft.images[field] !== "") return draft.images[field];
  if (field === "master_signature_sha256") return null; // never inherited (D19)
  const unit = field === "logo_sha256" ? "logo" : "stamp";
  const from = inherited(record, unit, organizationId);
  return typeof from?.value === "string" ? from.value : null;
}

export interface ImageTotal {
  /** Bytes of every image whose size is known. */
  knownBytes: number;
  /** Slots carrying an image whose size the read did not report. */
  unknownSlots: ImageField[];
  /** What a delivery may carry in all, with the approving doctor's signature at its cap. */
  budgetBytes: number;
  /** Room kept for the approving doctor's own signature, added at release. */
  approverReserveBytes: number;
}

/** The running total of image bytes a delivery from this organisation would carry. */
export function imageTotal(
  draft: BrandingDraft,
  record: BrandingRecord | undefined,
  info: ImageInfoByHash,
  organizationId: string,
): ImageTotal {
  let knownBytes = 0;
  const unknownSlots: ImageField[] = [];
  for (const field of IMAGE_FIELDS) {
    const sha = effectiveImage(draft, record, field, organizationId);
    if (sha === null) continue;
    const size = info[sha]?.byte_size;
    if (size === undefined) unknownSlots.push(field);
    else knownBytes += size;
  }
  return {
    knownBytes,
    unknownSlots,
    budgetBytes: DELIVERY_IMAGE_BUDGET_BYTES,
    approverReserveBytes: SIGNATURE_MAX_BYTES,
  };
}
